import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import Slider from "react-slick";
import { all_routes } from "../../router/all_routes";
import { handleApiError } from "../../../utility/handle-api-error";
import apiInstance from "../../../utility/apiInstance";
import { Country } from "../../../core/common/selectOption/json/selectOption";
import { ALPHA_REGEX, stripNonAlpha, ZIP_REGEX, stripNonZip, stripNonNumeric } from "../../../utility/inputValidators";

const hasNumber = (value: string): boolean => {
    return /[0-9]/.test(value);
};

const hasMixed = (value: string): boolean => {
    return /[a-z]/.test(value) && /[A-Z]/.test(value);
};

const hasSpecial = (value: string): boolean => {
    return /[!#@$%^&*)(+=._-]/.test(value);
};

const strengthColor = (count: number): string => {
    if (count < 1) return "poor";
    if (count < 2) return "weak";
    if (count < 3) return "strong";
    if (count < 4) return "heavy";
    return "poor"; // Default return to ensure it's always a string
};

const Register: React.FC = () => {

    interface RegisterData {
        firstName: string,
        lastName: string,
        email: string,
        password: string
    }

    const [activeTab, setActiveTab] = useState<"student" | "recruiter">("student");
    const [registerData, setRegisterData] = useState<RegisterData>({
        firstName: '',
        lastName: '',
        email: '',
        password: ''
    });

    const [error, setError] = useState<string | null>(null);
    const [errorFields, setErrorFields] = useState<string[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [termsCond, setTermsCond] = useState<boolean>(false);
    const [eye, setEye] = useState<boolean>(true);
    const [password, setPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [validationError, setValidationError] = useState<number>(0);
    const [strength, setStrength] = useState<string>("");
    const [eyeConfirmPassword, setEyeConfirmPassword] = useState<boolean>(true);

    // Recruiter form state
    interface RecruiterRegisterData {
        firstName: string;
        lastName: string;
        username: string;
        email: string;
        password: string;
        phone: string;
        companyName: string;
        ownerName: string;
        website: string;
        streetAddress: string;
        city: string;
        state: string;
        zipCode: string;
        country: string;
    }
    const [recruiterData, setRecruiterData] = useState<RecruiterRegisterData>({
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        password: '',
        phone: '',
        companyName: '',
        ownerName: '',
        website: '',
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
    });
    const [recruiterError, setRecruiterError] = useState<string | null>(null);
    const [recruiterErrorFields, setRecruiterErrorFields] = useState<string[]>([]);
    const [recruiterFieldErrors, setRecruiterFieldErrors] = useState<Record<string, string>>({});
    const [recruiterTermsCond, setRecruiterTermsCond] = useState<boolean>(false);
    const [recruiterEye, setRecruiterEye] = useState<boolean>(true);
    const [recruiterPassword, setRecruiterPassword] = useState<string>("");
    const [recruiterConfirmPassword, setRecruiterConfirmPassword] = useState<string>("");
    const [recruiterValidationError, setRecruiterValidationError] = useState<number>(0);
    const [recruiterStrength, setRecruiterStrength] = useState<string>("");
    const [recruiterEyeConfirmPassword, setRecruiterEyeConfirmPassword] = useState<boolean>(true);

    const route = all_routes;
    const navigate = useNavigate();

    const onEyeClick = () => {
        setEye((prev) => !prev);
    };

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = event.target.value;
        setPassword(newPassword);
        validatePassword(newPassword);
        handleInputChange(event);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {

        const { name } = event.target;
        let { value } = event.target;
        if (name === 'firstName' || name === 'lastName') {
            value = stripNonAlpha(value);
        }
        setRegisterData((prevData) => ({
            ...prevData,
            [name]: value
        }))
        if (errorFields.includes(name)) {
            setErrorFields((prev) => prev.filter((f) => f !== name));
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }

    }

    // Recruiter handlers
    const onRecruiterEyeClick = () => {
        setRecruiterEye((prev) => !prev);
    };

    const handleRecruiterPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = event.target.value;
        setRecruiterPassword(newPassword);
        validateRecruiterPassword(newPassword);
        handleRecruiterInputChange(event);
    };

    const handleRecruiterInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name } = event.target;
        let { value } = event.target;
        if (name === 'firstName' || name === 'lastName' || name === 'city' || name === 'state') {
            value = stripNonAlpha(value);
        } else if (name === 'zipCode') {
            value = stripNonZip(value);
        } else if (name === 'phone') {
            value = stripNonNumeric(value);
        }
        setRecruiterData((prevData) => ({
            ...prevData,
            [name]: value
        }))
        if (recruiterErrorFields.includes(name)) {
            setRecruiterErrorFields((prev) => prev.filter((f) => f !== name));
            setRecruiterFieldErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    }

    const validatePassword = (value: string) => {
        if (!value) {
            setValidationError(1);
        } else if (value.length < 8) {
            setValidationError(2);
        } else if (!/[0-9]/.test(value)) {
            setValidationError(3);
        } else if (!/[!@#$%^&*()]/.test(value)) {
            setValidationError(4);
        } else {
            setValidationError(5);
        }
    };

    const validateRecruiterPassword = (value: string) => {
        if (!value) {
            setRecruiterValidationError(1);
        } else if (value.length < 8) {
            setRecruiterValidationError(2);
        } else if (!/[0-9]/.test(value)) {
            setRecruiterValidationError(3);
        } else if (!/[!@#$%^&*()]/.test(value)) {
            setRecruiterValidationError(4);
        } else {
            setRecruiterValidationError(5);
        }
    };

    const loginSLider = {
        dots: true,
        infinite: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        adaptiveHeight: true,
        // autoplay: true, // Uncomment if needed
    };

    const messages = () => {
        switch (validationError) {
            case 2:
                return (
                    <span
                        id="poor"
                        className="active mt-2"
                        style={{ fontSize: 14, color: "#DC3545", marginTop: "8px" }}
                    >
                        <ImageWithBasePath
                            src="assets/img/icon/angry.svg"
                            className="me-2"
                            alt=""
                        />{" "}
                        Weak. Must contain at least 8 characters
                    </span>
                );
            case 3:
                return (
                    <span
                        id="weak"
                        className="active  mt-2"
                        style={{ fontSize: 14, color: "#FFC107", marginTop: "8px" }}
                    >
                        <ImageWithBasePath
                            src="assets/img/icon/anguish.svg"
                            className="me-2"
                            alt=""
                        />{" "}
                        Average. Must contain at least 1 letter or number
                    </span>
                );
            case 4:
                return (
                    <span
                        id="strong"
                        className="active  mt-2"
                        style={{ fontSize: 14, color: "#0D6EFD", marginTop: "8px" }}
                    >
                        <ImageWithBasePath
                            src="assets/img/icon/smile.svg"
                            className="me-2"
                            alt=""
                        />{" "}
                        Almost. Must contain special symbol
                    </span>
                );
            case 5:
                return (
                    <span
                        id="heavy"
                        className="active  mt-2"
                        style={{ fontSize: 14, color: "#4BB543", marginTop: "8px" }}
                    >
                        <ImageWithBasePath
                            src="assets/img/icon/smile.svg"
                            className="me-2"
                            alt=""
                        />{" "}
                        Awesome! You have a secure password.
                    </span>
                );
            default:
                return null;
        }
    };

    const recruiterMessages = () => {
        switch (recruiterValidationError) {
            case 2:
                return (
                    <span
                        id="poor"
                        className="active mt-2"
                        style={{ fontSize: 14, color: "#DC3545", marginTop: "8px" }}
                    >
                        <ImageWithBasePath
                            src="assets/img/icon/angry.svg"
                            className="me-2"
                            alt=""
                        />{" "}
                        Weak. Must contain at least 8 characters
                    </span>
                );
            case 3:
                return (
                    <span
                        id="weak"
                        className="active  mt-2"
                        style={{ fontSize: 14, color: "#FFC107", marginTop: "8px" }}
                    >
                        <ImageWithBasePath
                            src="assets/img/icon/anguish.svg"
                            className="me-2"
                            alt=""
                        />{" "}
                        Average. Must contain at least 1 letter or number
                    </span>
                );
            case 4:
                return (
                    <span
                        id="strong"
                        className="active  mt-2"
                        style={{ fontSize: 14, color: "#0D6EFD", marginTop: "8px" }}
                    >
                        <ImageWithBasePath
                            src="assets/img/icon/smile.svg"
                            className="me-2"
                            alt=""
                        />{" "}
                        Almost. Must contain special symbol
                    </span>
                );
            case 5:
                return (
                    <span
                        id="heavy"
                        className="active  mt-2"
                        style={{ fontSize: 14, color: "#4BB543", marginTop: "8px" }}
                    >
                        <ImageWithBasePath
                            src="assets/img/icon/smile.svg"
                            className="me-2"
                            alt=""
                        />{" "}
                        Awesome! You have a secure password.
                    </span>
                );
            default:
                return null;
        }
    };

    const strengthIndicator = (value: string): number => {
        let strengths = 0;
        if (value.length >= 8) strengths = 1;
        if (hasNumber(value) && value.length >= 8) strengths = 2;
        if (hasSpecial(value) && value.length >= 8 && hasNumber(value))
            strengths = 3;
        if (
            hasMixed(value) &&
            hasSpecial(value) &&
            value.length >= 8 &&
            hasNumber(value)
        )
            strengths = 3;
        return strengths;
    };

    useEffect(() => {
        if (password) {
            let strengthValue = strengthIndicator(password);
            let color = strengthColor(strengthValue);
            setStrength(color);
        } else {
            setStrength("");
        }
    }, [password]);

    useEffect(() => {
        if (recruiterPassword) {
            let strengthValue = strengthIndicator(recruiterPassword);
            let color = strengthColor(strengthValue);
            setRecruiterStrength(color);
        } else {
            setRecruiterStrength("");
        }
    }, [recruiterPassword]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);

        const firstNameInvalid = !registerData.firstName || !ALPHA_REGEX.test(registerData.firstName);
        if (firstNameInvalid || !registerData.email || !registerData.password) {
            const invalid: string[] = [];
            const messages: Record<string, string> = {};
            if (firstNameInvalid) {
                invalid.push('firstName');
                messages.firstName = 'A valid first name is required.';
            }
            if (!registerData.email) {
                invalid.push('email');
                messages.email = 'Email is required.';
            }
            if (!registerData.password) {
                invalid.push('password');
                messages.password = 'Password is required.';
            }
            setErrorFields(invalid);
            setFieldErrors(messages);
            return;
        }
        setErrorFields([]);
        setFieldErrors({});

        if (registerData.password !== confirmPassword) {
            setErrorFields(['confirmPassword']);
            setFieldErrors({ confirmPassword: "Passwords didn't match!" });
            return;
        }

        if (!termsCond) {
            setError("Please agree to Terms of Service and Privacy Policy.");
            return;
        }

        try {
            await apiInstance.post('/api/users', { ...registerData, role: 'student' });
            navigate(route.otp, { state: { email: registerData.email } });

        } catch (error: any) {
            setError(handleApiError(error));
        }
    };

    const handleRecruiterSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setRecruiterError(null);

        const requiredFields: (keyof RecruiterRegisterData)[] = [
            'firstName', 'lastName', 'username', 'email', 'phone',
            'companyName', 'ownerName', 'streetAddress', 'city', 'state', 'zipCode', 'country', 'password',
        ];
        const alphaFields: (keyof RecruiterRegisterData)[] = ['firstName', 'lastName', 'city', 'state'];
        const fieldLabels: Record<string, string> = {
            firstName: 'First name', lastName: 'Last name', username: 'Username', email: 'Email',
            phone: 'Mobile number', companyName: 'Company name', ownerName: 'Owner name',
            streetAddress: 'Street address', city: 'City', state: 'State / Province',
            zipCode: 'Zip / Postal code', country: 'Country', password: 'Password',
        };
        const invalidFields = requiredFields.filter((f) => {
            if (!recruiterData[f]) return true;
            if (alphaFields.includes(f)) return !ALPHA_REGEX.test(recruiterData[f]);
            if (f === 'zipCode') return !ZIP_REGEX.test(recruiterData[f]);
            if (f === 'username') return !/^[a-zA-Z0-9_.]+$/.test(recruiterData[f]);
            return false;
        });
        if (invalidFields.length > 0) {
            const messages: Record<string, string> = {};
            invalidFields.forEach((f) => {
                if (f === 'username' && recruiterData[f] && !/^[a-zA-Z0-9_.]+$/.test(recruiterData[f])) {
                    messages[f] = 'Username may only contain letters, numbers, underscores (_) and periods (.).';
                } else {
                    messages[f] = !recruiterData[f]
                        ? `${fieldLabels[f]} is required.`
                        : `A valid ${fieldLabels[f].toLowerCase()} is required.`;
                }
            });
            setRecruiterErrorFields(invalidFields);
            setRecruiterFieldErrors(messages);
            return;
        }
        setRecruiterErrorFields([]);
        setRecruiterFieldErrors({});

        if (recruiterData.password !== recruiterConfirmPassword) {
            setRecruiterErrorFields(['confirmPassword']);
            setRecruiterFieldErrors({ confirmPassword: "Passwords didn't match!" });
            return;
        }

        if (!recruiterTermsCond) {
            setRecruiterError("Please agree to Terms of Service and Privacy Policy.");
            return;
        }

        try {
            await apiInstance.post('/api/users', {
                firstName: recruiterData.firstName,
                lastName: recruiterData.lastName,
                username: recruiterData.username,
                email: recruiterData.email,
                password: recruiterData.password,
                phone: recruiterData.phone,
                role: 'recruiter',
                businessInfo: {
                    companyName: recruiterData.companyName,
                    ownerName: recruiterData.ownerName,
                    website: recruiterData.website,
                    streetAddress: recruiterData.streetAddress,
                    city: recruiterData.city,
                    state: recruiterData.state,
                    zipCode: recruiterData.zipCode,
                    country: recruiterData.country,
                },
            });
            navigate(route.otp, { state: { email: recruiterData.email } });

        } catch (error: any) {
            setRecruiterError(handleApiError(error));
        }
    };

    return (
        <>
            {/* Main Wrapper */}
            <div className="main-wrapper">
                <div className="login-content">
                    <div className="row">
                        {/* Login Banner */}
                        <div className="col-md-6 login-bg d-none d-lg-flex">
                            <Slider {...loginSLider} className="login-carousel">
                                <div>
                                    <div className="login-carousel-section mb-3">
                                        <div className="login-banner">
                                            <ImageWithBasePath
                                                src="assets/img/auth/auth-1.svg"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                        </div>
                                        <div className="mentor-course text-center">
                                            <h3 className="mb-2">
                                                Welcome to <br />
                                                Insta<span className="text-secondary">Admissions</span>{" "}
                                                Programs.
                                            </h3>
                                            <p>
                                                A platform built to empower institutions, educators, and students—designed to manage, deliver, and track academic programs and training with ease and precision.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="login-carousel-section mb-3">
                                        <div className="login-banner">
                                            <ImageWithBasePath
                                                src="assets/img/index-3/banner-01.png"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                        </div>
                                        <div className="mentor-course text-center">
                                            <h3 className="mb-2">
                                                Create Your Account <br />
                                                in Minutes
                                            </h3>
                                            <p>
                                                A next-generation platform crafted to streamline how institutions and educators offer learning—enabling seamless delivery, tracking, and management of academic and skill-based programs for students worldwide.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="login-carousel-section mb-3">
                                        <div className="login-banner">
                                            <ImageWithBasePath
                                                src="assets/img/index-3/register-img.png"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                        </div>
                                        <div className="mentor-course text-center">
                                            <h3 className="mb-2">
                                                Join a Growing <br />
                                                Global Community
                                            </h3>
                                            <p>
                                                Transform how you educate with a centralized system that simplifies program administration, enhances engagement, and provides real-time insights into learner progress—built for institutions ready to scale.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Slider>
                        </div>
                        {/* /Login Banner */}
                        <div className="col-md-6 login-wrap-bg">
                            {/* Login */}
                            <div className="login-wrapper">
                                <div className="loginbox">
                                    <div className="w-100">
                                        <div className="d-flex align-items-center justify-content-between login-header">
                                            <ImageWithBasePath
                                                src="assets/img/logo-white.png"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                            <Link to={route.homesix} className="link-1">
                                                Back to Home
                                            </Link>
                                        </div>
                                        <h1 className="fs-32 fw-bold topic">Sign up</h1>
                                        <div className="d-flex gap-2 mb-3">
                                            <button type="button" className={`btn ${activeTab === 'student' ? 'btn-secondary' : 'btn-light'}`} onClick={() => setActiveTab('student')}>Student</button>
                                            <button type="button" className={`btn ${activeTab === 'recruiter' ? 'btn-secondary' : 'btn-light'}`} onClick={() => setActiveTab('recruiter')}>Recruitment Partner</button>
                                        </div>
                                        {activeTab === 'student' && (
                                        <form onSubmit={handleSubmit} className="mb-3 pb-3">
                                            <div className="mb-3 position-relative">
                                                <label className="form-label">
                                                    First Name<span className="text-danger ms-1">*</span>
                                                </label>
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        name="firstName"
                                                        value={registerData.firstName}
                                                        onChange={handleInputChange}
                                                        className={`form-control form-control-lg ${errorFields.includes('firstName') ? 'is-invalid' : ''}`}
                                                    />
                                                    <span>
                                                        <i className="isax isax-user input-icon text-gray-7 fs-14" />
                                                    </span>
                                                </div>
                                                {fieldErrors.firstName && (
                                                    <div className="invalid-feedback d-block">{fieldErrors.firstName}</div>
                                                )}
                                            </div>
                                            <div className="mb-3 position-relative">
                                                <label className="form-label">
                                                    Last Name<span className="text-danger ms-1"></span>
                                                </label>
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        name="lastName"
                                                        value={registerData.lastName}
                                                        onChange={handleInputChange}
                                                        className="form-control form-control-lg"
                                                    />
                                                    <span>
                                                        <i className="isax isax-user input-icon text-gray-7 fs-14" />
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="mb-3 position-relative">
                                                <label className="form-label">
                                                    Email<span className="text-danger ms-1">*</span>
                                                </label>
                                                <div className="position-relative">
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        value={registerData.email}
                                                        onChange={handleInputChange}
                                                        className={`form-control form-control-lg ${errorFields.includes('email') ? 'is-invalid' : ''}`}
                                                    />
                                                    <span>
                                                        <i className="isax isax-sms input-icon text-gray-7 fs-14" />
                                                    </span>
                                                </div>
                                                {fieldErrors.email && (
                                                    <div className="invalid-feedback d-block">{fieldErrors.email}</div>
                                                )}
                                            </div>
                                            <div className="mb-3 position-relative">
                                                New Password <span className="text-danger"> *</span>
                                                <label className="form-label">
                                                </label>
                                                <div className="position-relative" id="passwordInput">
                                                    <input
                                                        className={`form-control pass-input ${errorFields.includes('password') ? 'is-invalid' : ''}`}
                                                        type={eye ? "password" : "text"}
                                                        onChange={handlePasswordChange}
                                                        name="password"
                                                        value={registerData.password}
                                                    />
                                                    <span
                                                        onClick={onEyeClick}
                                                        className={`toggle-passwords text-gray-7 fs-14 isax isax-eye-slash" ${eye ? "isax-eye-slash" : "isax-eye"
                                                            }`}
                                                    />
                                                </div>
                                                <div
                                                    id="passwordStrength"
                                                    style={{ display: "flex" }}
                                                    className={`password-strength ${strength === "poor"
                                                        ? "poor-active"
                                                        : strength === "weak"
                                                            ? "avg-active"
                                                            : strength === "strong"
                                                                ? "strong-active"
                                                                : strength === "heavy"
                                                                    ? "heavy-active"
                                                                    : ""
                                                        }`}
                                                >
                                                    <span id="poor" className="active"></span>
                                                    <span id="weak" className="active"></span>
                                                    <span id="strong" className="active"></span>
                                                    <span id="heavy" className="active"></span>
                                                </div>
                                                <div id="passwordInfo">{messages()}</div>
                                                {fieldErrors.password && (
                                                    <div className="invalid-feedback d-block">{fieldErrors.password}</div>
                                                )}
                                            </div>
                                            <div className="mb-3 position-relative">
                                                <label className="form-label">
                                                    Confirm Password{" "}
                                                    <span className="text-danger"> *</span>
                                                </label>
                                                <div className="position-relative">
                                                    <input
                                                        type={eyeConfirmPassword ? "password" : "text"}
                                                        className={`pass-inputa form-control form-control-lg ${errorFields.includes('confirmPassword') ? 'is-invalid' : ''}`}
                                                        value={confirmPassword}
                                                        onChange={(e) => {
                                                            setConfirmPassword(e.target.value);
                                                            if (errorFields.includes('confirmPassword')) {
                                                                setErrorFields((prev) => prev.filter((f) => f !== 'confirmPassword'));
                                                                setFieldErrors((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next.confirmPassword;
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                    />
                                                    <span
                                                        className={`isax toggle-passworda ${eyeConfirmPassword ? "isax-eye-slash" : "isax-eye"
                                                            } text-gray-7 fs-14`}
                                                        onClick={() =>
                                                            setEyeConfirmPassword((prev) => !prev)
                                                        }
                                                        style={{
                                                            cursor: "pointer",
                                                            position: "absolute",
                                                            right: "10px",
                                                            top: "50%",
                                                            transform: "translateY(-50%)",
                                                        }}
                                                    />
                                                </div>
                                                {fieldErrors.confirmPassword && (
                                                    <div className="invalid-feedback d-block">{fieldErrors.confirmPassword}</div>
                                                )}
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between mb-4">
                                                <div className="remember-me d-flex align-items-center">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        defaultValue=""
                                                        id="flexCheckDefault"
                                                        checked={(termsCond)}
                                                        onChange={(e) => { setTermsCond((prev) => (!prev)) }}
                                                    />
                                                    <label
                                                        className="form-check-label mb-0 d-inline-flex remember-me fs-14"
                                                        htmlFor="flexCheckDefault"
                                                    >
                                                        I agree with{" "}
                                                        <Link to={route.termsConditions} className="link-2 mx-2">
                                                            Terms of Service
                                                        </Link>{" "}
                                                        and{" "}
                                                        <Link to={route.privacyPolicy} className="link-2 mx-2">
                                                            Privacy Policy
                                                        </Link>
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="d-grid">
                                                <button
                                                    className="btn btn-secondary btn-lg"
                                                    type="submit"
                                                >
                                                    Sign Up <i className="isax isax-arrow-right-3 ms-1" />
                                                </button>
                                            </div>
                                            <div className="mt-3 text-red fs-14 fw-normal d-flex align-items-center justify-content-center">
                                                {error}
                                            </div>
                                        </form>
                                        )}
                                        {activeTab === 'recruiter' && (
                                        <form onSubmit={handleRecruiterSubmit} className="mb-3 pb-3">
                                            
                                            {/* First Name + Last Name */}
                                            <div className="row g-3 mb-3">
                                                <div className="col-6">
                                                    <label className="form-label">First Name<span className="text-danger ms-1">*</span></label>
                                                    <div className="position-relative">
                                                        <input
                                                            type="text"
                                                            name="firstName"
                                                            value={recruiterData.firstName}
                                                            onChange={handleRecruiterInputChange}
                                                            className={`form-control form-control-lg ${recruiterErrorFields.includes('firstName') ? 'is-invalid' : ''}`}
                                                        />
                                                        <span><i className="isax isax-user input-icon text-gray-7 fs-14" /></span>
                                                    </div>
                                                    {recruiterFieldErrors.firstName && (
                                                        <div className="invalid-feedback d-block">{recruiterFieldErrors.firstName}</div>
                                                    )}
                                                </div>
                                                <div className="col-6">
                                                    <label className="form-label">Last Name<span className="text-danger ms-1">*</span></label>
                                                    <div className="position-relative">
                                                        <input
                                                            type="text"
                                                            name="lastName"
                                                            value={recruiterData.lastName}
                                                            onChange={handleRecruiterInputChange}
                                                            className={`form-control form-control-lg ${recruiterErrorFields.includes('lastName') ? 'is-invalid' : ''}`}
                                                        />
                                                        <span><i className="isax isax-user input-icon text-gray-7 fs-14" /></span>
                                                    </div>
                                                    {recruiterFieldErrors.lastName && (
                                                        <div className="invalid-feedback d-block">{recruiterFieldErrors.lastName}</div>
                                                    )}
                                                </div>
                                            </div>
                                            {/* Email + Username */}
                                            <div className="row g-3 mb-3">
                                                <div className="col-6">
                                                    <label className="form-label">Email<span className="text-danger ms-1">*</span></label>
                                                    <div className="position-relative">
                                                        <input
                                                            type="email"
                                                            name="email"
                                                            value={recruiterData.email}
                                                            onChange={handleRecruiterInputChange}
                                                            className={`form-control form-control-lg ${recruiterErrorFields.includes('email') ? 'is-invalid' : ''}`}
                                                        />
                                                        <span><i className="isax isax-sms input-icon text-gray-7 fs-14" /></span>
                                                    </div>
                                                    {recruiterFieldErrors.email && (
                                                        <div className="invalid-feedback d-block">{recruiterFieldErrors.email}</div>
                                                    )}
                                                </div>
                                                <div className="col-6">
                                                    <label className="form-label">Username<span className="text-danger ms-1">*</span></label>
                                                    <div className="position-relative">
                                                        <input
                                                            type="text"
                                                            name="username"
                                                            value={recruiterData.username}
                                                            onChange={handleRecruiterInputChange}
                                                            className={`form-control form-control-lg ${recruiterErrorFields.includes('username') ? 'is-invalid' : ''}`}
                                                        />
                                                        <span><i className="isax isax-user input-icon text-gray-7 fs-14" /></span>
                                                    </div>
                                                    {recruiterFieldErrors.username && (
                                                        <div className="invalid-feedback d-block">{recruiterFieldErrors.username}</div>
                                                    )}
                                                </div>
                                            </div>
                                            {/* Mobile No */}
                                            <div className="row g-3 mb-3">
                                                <div className="col-6">
                                                    <label className="form-label">Mobile No<span className="text-danger ms-1">*</span></label>
                                                    <div className="position-relative">
                                                        <input
                                                            type="text"
                                                            name="phone"
                                                            value={recruiterData.phone}
                                                            onChange={handleRecruiterInputChange}
                                                            className={`form-control form-control-lg ${recruiterErrorFields.includes('phone') ? 'is-invalid' : ''}`}
                                                        />
                                                        <span><i className="isax isax-call input-icon text-gray-7 fs-14" /></span>
                                                    </div>
                                                    {recruiterFieldErrors.phone && (
                                                        <div className="invalid-feedback d-block">{recruiterFieldErrors.phone}</div>
                                                    )}
                                                </div>
                                            </div>
                                            {/* ── Business Information ── */}
                                            <div className="d-flex align-items-center mb-3 mt-1">
                                                <p className="mb-0 me-2 fs-14 fw-semibold text-gray-9 text-nowrap">Business Information</p>
                                                <hr className="flex-grow-1 m-0" />
                                            </div>
                                            {/* Company Name + Owner Name */}
                                            <div className="row g-3 mb-3">
                                                <div className="col-6">
                                                    <label className="form-label">Company Name<span className="text-danger ms-1">*</span></label>
                                                    <div className="position-relative">
                                                        <input
                                                            type="text"
                                                            name="companyName"
                                                            value={recruiterData.companyName}
                                                            onChange={handleRecruiterInputChange}
                                                            className={`form-control form-control-lg ${recruiterErrorFields.includes('companyName') ? 'is-invalid' : ''}`}
                                                        />
                                                        <span><i className="isax isax-building input-icon text-gray-7 fs-14" /></span>
                                                    </div>
                                                    {recruiterFieldErrors.companyName && (
                                                        <div className="invalid-feedback d-block">{recruiterFieldErrors.companyName}</div>
                                                    )}
                                                </div>
                                                <div className="col-6">
                                                    <label className="form-label">Owner Name<span className="text-danger ms-1">*</span></label>
                                                    <div className="position-relative">
                                                        <input
                                                            type="text"
                                                            name="ownerName"
                                                            value={recruiterData.ownerName}
                                                            onChange={handleRecruiterInputChange}
                                                            className={`form-control form-control-lg ${recruiterErrorFields.includes('ownerName') ? 'is-invalid' : ''}`}
                                                        />
                                                        <span><i className="isax isax-user input-icon text-gray-7 fs-14" /></span>
                                                    </div>
                                                    {recruiterFieldErrors.ownerName && (
                                                        <div className="invalid-feedback d-block">{recruiterFieldErrors.ownerName}</div>
                                                    )}
                                                </div>
                                            </div>
                                            {/* Website */}
                                            <div className="row g-3 mb-3">
                                                <div className="col-6">
                                                    <label className="form-label">Website</label>
                                                    <div className="position-relative">
                                                        <input
                                                            type="text"
                                                            name="website"
                                                            value={recruiterData.website}
                                                            onChange={handleRecruiterInputChange}
                                                            className="form-control form-control-lg"
                                                        />
                                                        <span><i className="isax isax-global input-icon text-gray-7 fs-14" /></span>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Street Address — full width */}
                                            <div className="mb-3">
                                                <label className="form-label">Street Address<span className="text-danger ms-1">*</span></label>
                                                <textarea
                                                    name="streetAddress"
                                                    value={recruiterData.streetAddress}
                                                    onChange={handleRecruiterInputChange}
                                                    className={`form-control form-control-lg ${recruiterErrorFields.includes('streetAddress') ? 'is-invalid' : ''}`}
                                                    rows={2}
                                                />
                                                {recruiterFieldErrors.streetAddress && (
                                                    <div className="invalid-feedback d-block">{recruiterFieldErrors.streetAddress}</div>
                                                )}
                                            </div>
                                            {/* City + State */}
                                            <div className="row g-3 mb-3">
                                                <div className="col-6">
                                                    <label className="form-label">City<span className="text-danger ms-1">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="city"
                                                        value={recruiterData.city}
                                                        onChange={handleRecruiterInputChange}
                                                        className={`form-control form-control-lg ${recruiterErrorFields.includes('city') ? 'is-invalid' : ''}`}
                                                    />
                                                    {recruiterFieldErrors.city && (
                                                        <div className="invalid-feedback d-block">{recruiterFieldErrors.city}</div>
                                                    )}
                                                </div>
                                                <div className="col-6">
                                                    <label className="form-label">State / Province<span className="text-danger ms-1">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="state"
                                                        value={recruiterData.state}
                                                        onChange={handleRecruiterInputChange}
                                                        className={`form-control form-control-lg ${recruiterErrorFields.includes('state') ? 'is-invalid' : ''}`}
                                                    />
                                                    {recruiterFieldErrors.state && (
                                                        <div className="invalid-feedback d-block">{recruiterFieldErrors.state}</div>
                                                    )}
                                                </div>
                                            </div>
                                            {/* Zip Code + Country */}
                                            <div className="row g-3 mb-3">
                                                <div className="col-6">
                                                    <label className="form-label">Zip / Postal Code<span className="text-danger ms-1">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="zipCode"
                                                        value={recruiterData.zipCode}
                                                        onChange={handleRecruiterInputChange}
                                                        className={`form-control form-control-lg ${recruiterErrorFields.includes('zipCode') ? 'is-invalid' : ''}`}
                                                    />
                                                    {recruiterFieldErrors.zipCode && (
                                                        <div className="invalid-feedback d-block">{recruiterFieldErrors.zipCode}</div>
                                                    )}
                                                </div>
                                                <div className="col-6">
                                                    <label className="form-label">Country<span className="text-danger ms-1">*</span></label>
                                                    <select
                                                        name="country"
                                                        value={recruiterData.country}
                                                        onChange={(e) => {
                                                            setRecruiterData((prev) => ({ ...prev, country: e.target.value }));
                                                            if (recruiterErrorFields.includes('country')) {
                                                                setRecruiterErrorFields((prev) => prev.filter((f) => f !== 'country'));
                                                                setRecruiterFieldErrors((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next.country;
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                        className={`form-control form-control-lg ${recruiterErrorFields.includes('country') ? 'is-invalid' : ''}`}
                                                    >
                                                        <option value="">Select Country</option>
                                                        {Country.map((c) => (
                                                            <option key={c.value} value={c.value}>{c.label}</option>
                                                        ))}
                                                    </select>
                                                    {recruiterFieldErrors.country && (
                                                        <div className="invalid-feedback d-block">{recruiterFieldErrors.country}</div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Password + Confirm Password */}
                                            <div className="row g-3 mb-3">
                                                <div className="col-6">
                                                    <label className="form-label">New Password<span className="text-danger ms-1">*</span></label>
                                                    <div className="position-relative" id="passwordInput">
                                                        <input
                                                            className={`form-control pass-input ${recruiterErrorFields.includes('password') ? 'is-invalid' : ''}`}
                                                            type={recruiterEye ? "password" : "text"}
                                                            onChange={handleRecruiterPasswordChange}
                                                            name="password"
                                                            value={recruiterData.password}
                                                        />
                                                        <span
                                                            onClick={onRecruiterEyeClick}
                                                            className={`toggle-passwords text-gray-7 fs-14 isax isax-eye-slash" ${recruiterEye ? "isax-eye-slash" : "isax-eye"}`}
                                                        />
                                                    </div>
                                                    <div
                                                        id="passwordStrength"
                                                        style={{ display: "flex" }}
                                                        className={`password-strength ${recruiterStrength === "poor" ? "poor-active" : recruiterStrength === "weak" ? "avg-active" : recruiterStrength === "strong" ? "strong-active" : recruiterStrength === "heavy" ? "heavy-active" : ""}`}
                                                    >
                                                        <span id="poor" className="active"></span>
                                                        <span id="weak" className="active"></span>
                                                        <span id="strong" className="active"></span>
                                                        <span id="heavy" className="active"></span>
                                                    </div>
                                                    <div id="passwordInfo">{recruiterMessages()}</div>
                                                    {recruiterFieldErrors.password && (
                                                        <div className="invalid-feedback d-block">{recruiterFieldErrors.password}</div>
                                                    )}
                                                </div>
                                                <div className="col-6">
                                                    <label className="form-label">Confirm Password<span className="text-danger ms-1">*</span></label>
                                                    <div className="position-relative">
                                                        <input
                                                            type={recruiterEyeConfirmPassword ? "password" : "text"}
                                                            className={`pass-inputa form-control form-control-lg ${recruiterErrorFields.includes('confirmPassword') ? 'is-invalid' : ''}`}
                                                            value={recruiterConfirmPassword}
                                                            onChange={(e) => {
                                                                setRecruiterConfirmPassword(e.target.value);
                                                                if (recruiterErrorFields.includes('confirmPassword')) {
                                                                    setRecruiterErrorFields((prev) => prev.filter((f) => f !== 'confirmPassword'));
                                                                    setRecruiterFieldErrors((prev) => {
                                                                        const next = { ...prev };
                                                                        delete next.confirmPassword;
                                                                        return next;
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                        <span
                                                            className={`isax toggle-passworda ${recruiterEyeConfirmPassword ? "isax-eye-slash" : "isax-eye"} text-gray-7 fs-14`}
                                                            onClick={() => setRecruiterEyeConfirmPassword((prev) => !prev)}
                                                            style={{ cursor: "pointer", position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)" }}
                                                        />
                                                    </div>
                                                    {recruiterFieldErrors.confirmPassword && (
                                                        <div className="invalid-feedback d-block">{recruiterFieldErrors.confirmPassword}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between mb-4">
                                                <div className="remember-me d-flex align-items-center">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        defaultValue=""
                                                        id="flexCheckDefaultRecruiter"
                                                        checked={(recruiterTermsCond)}
                                                        onChange={(e) => { setRecruiterTermsCond((prev) => (!prev)) }}
                                                    />
                                                    <label
                                                        className="form-check-label mb-0 d-inline-flex remember-me fs-14"
                                                        htmlFor="flexCheckDefaultRecruiter"
                                                    >
                                                        I agree with{" "}
                                                        <Link to={route.termsConditions} className="link-2 mx-2">
                                                            Terms of Service
                                                        </Link>{" "}
                                                        and{" "}
                                                        <Link to={route.privacyPolicy} className="link-2 mx-2">
                                                            Privacy Policy
                                                        </Link>
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="d-grid">
                                                <button
                                                    className="btn btn-secondary btn-lg"
                                                    type="submit"
                                                >
                                                    Sign Up <i className="isax isax-arrow-right-3 ms-1" />
                                                </button>
                                            </div>
                                            <div className="mt-3 text-red fs-14 fw-normal d-flex align-items-center justify-content-center">
                                                {recruiterError}
                                            </div>
                                        </form>
                                        )}
                                        {/* <div className="d-flex align-items-center justify-content-center or fs-14 mb-3">
                                            Or
                                        </div>
                                        <div className="d-flex align-items-center justify-content-center mb-3">
                                            <Link to="#" className="btn btn-light me-2">
                                                <ImageWithBasePath
                                                    src="assets/img/icons/google.svg"
                                                    alt="img"
                                                    className="me-2"
                                                />
                                                Google
                                            </Link>
                                            <Link to="#" className="btn btn-light">
                                                <ImageWithBasePath
                                                    src="assets/img/icons/facebook.svg"
                                                    alt="img"
                                                    className="me-2"
                                                />
                                                Facebook
                                            </Link>
                                        </div> */}
                                        <div className="fs-14 fw-normal d-flex align-items-center justify-content-center">
                                            Already you have an account?
                                            <Link to={route.login} className="link-2 ms-1">
                                                {" "}
                                                Login
                                            </Link>
                                        </div>
                                        {/* /Login */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* /Main Wrapper */}
        </>
    );
};

export default Register;
